import {ChangeDetectionStrategy, Component, computed, inject, OnInit, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle} from '@ng-bootstrap/ng-bootstrap';
import {TradeStore} from '../../../pages/trade/trade.store';
import {CurrentUserContextService} from '../../services/current-user-context.service';
import {RoleAssignmentDto, UserRole} from '../../../../models/generated-models';
import {Router} from '@angular/router';

@Component({
  selector: 'app-account-switcher',
  standalone: true,
  imports: [
    CommonModule,
    NgbDropdown,
    NgbDropdownToggle,
    NgbDropdownMenu,
    NgbDropdownItem,
  ],
  templateUrl: './account-switcher.component.html',
  styleUrl: './account-switcher.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountSwitcherComponent implements OnInit {
  protected readonly store = inject(TradeStore);
  private readonly currentUser = inject(CurrentUserContextService);
  private readonly router = inject(Router);

  /**
   * Shown to a ตัวแทนดำเนินการ — by role, or as soon as an accepted grant exists, so the switcher
   * appears right after accepting an invitation even before the refreshed token carries the role
   * (work item 173).
   */
  protected readonly isAgent = computed(() =>
    this.currentUser.hasUserRole('AGENT_OPERATOR') || this.store.receivedAssignments().length > 0);
  protected readonly searchText = signal('');

  protected readonly filteredAssignments = computed(() => {
    const query = this.searchText().trim().toLowerCase();
    const items = this.store.receivedAssignments();
    if (!query) {
      return items;
    }
    return items.filter((item) => [item.grantorName, item.grantorTaxId, item.businessName, item.businessCode, item.standardName, item.standardCode]
      .some((field) => (field ?? '').toLowerCase().includes(query)));
  });

  protected readonly showMyAccount = computed(() => {
    const query = this.searchText().trim().toLowerCase();
    if (!query) {
      return true;
    }
    return 'บัญชีของฉัน (ตัวแทน)'.toLowerCase().includes(query);
  });

  ngOnInit(): void {
    this.currentUser.load().subscribe({
      error: () => {
      }
    });
    this.store.restoreActiveAssignment();
  }

  protected get selectedAssignment(): RoleAssignmentDto | null {
    return this.store.activeRoleAssignment();
  }

  protected get selectedAssignmentLabel(): string {
    const assignment = this.selectedAssignment;
    if (!assignment) {
      return 'บัญชีของฉัน (ตัวแทน)';
    }
    const name = assignment.grantorName || assignment.grantorTaxId || 'ผู้มอบสิทธิ์';
    const business = assignment.businessName ? ` (${assignment.businessName})` : '';
    const cert = assignment.standardName || assignment.standardCode ? ` - ${assignment.standardName || assignment.standardCode}` : '';
    return `${name}${business}${cert}`;
  }

  protected roleLabel(role?: UserRole): string {
    return role ? UserRole[role] : '';
  }

  protected selectRoleAssignment(assignment: RoleAssignmentDto | null, dropdown?: NgbDropdown): void {
    this.searchText.set('');
    dropdown?.close();
    this.store.selectActiveRoleAssignment(assignment).finally(() => {
      // Reload authorities from backend so UI (sidebar, navigation items, role directives)
      // picks up the delegated authorities (e.g. FARMER_AGENT / MIDDLEMAN_AGENT) and re-renders.
      this.currentUser.refresh().subscribe({
        next: () => {
          this.reloadAndRedirectToProfile();
        },
        error: () => {
          this.reloadAndRedirectToProfile();
        }
      });
    });
  }

  private reloadAndRedirectToProfile(): void {
    const target = '/ui/profile';
    const isProfile = window.location.pathname.replace(/\/+$/, '') === target
      || this.router.url.split('?')[0].split('#')[0] === '/profile';
    if (isProfile) {
      window.location.reload();
    } else {
      window.location.href = target;
    }
  }

  protected onSearchInput(event: Event): void {
    this.searchText.set((event.target as HTMLInputElement).value ?? '');
  }
}
